import { nanoid } from 'nanoid';
import { StoreApi } from 'zustand';

import { HierarchicalLabelOrFolder, LabelsStore } from './types';
import { getItemId } from '../../../utils/getItemIdForLabelsAndFolders';

type ZustandSet = StoreApi<LabelsStore>['setState'];
type ZustandGet = StoreApi<LabelsStore>['getState'];

export const handleSetInboxLabels = (
  set: ZustandSet,
  labels: HierarchicalLabelOrFolder[],
) => {
  set({ labels });
};

export const handleSetLoadingFetchLabels = (
  set: ZustandSet,
  isFetchLoading: boolean,
) => {
  set({ isFetchLoading });
};

const findLabelRecursive = (
  id: string,
  labels?: HierarchicalLabelOrFolder[],
): HierarchicalLabelOrFolder | undefined => {
  if (!labels) {
    return undefined;
  }

  for (const label of labels) {
    if (getItemId(label) === id) {
      return label;
    }

    if (label.children?.length) {
      const found = findLabelRecursive(
        id,
        label.children as HierarchicalLabelOrFolder[],
      );
      if (found) return found;
    }
  }

  return undefined;
};

export const handleGetLabelByIdAndEmailAddress = (
  get: ZustandGet,
  id: string,
  emailAddress: string,
): HierarchicalLabelOrFolder | undefined => {
  const { labels } = get();

  const labelsForAddress = labels.filter(
    (label) => label.emailAddress === emailAddress,
  );

  return findLabelRecursive(id, labelsForAddress);
};

const updateLabelRecursive = (
  labels: HierarchicalLabelOrFolder[],
  id: string,
  emailAddress: string,
  data: Partial<HierarchicalLabelOrFolder>,
): HierarchicalLabelOrFolder[] => {
  return labels.map((label) => {
    if (label.emailAddress !== emailAddress) {
      return label;
    }

    if (getItemId(label) === id) {
      return { ...label, ...data };
    }

    if (label.children && label.children.length > 0) {
      return {
        ...label,
        children: updateLabelRecursive(
          label.children as HierarchicalLabelOrFolder[],
          id,
          emailAddress,
          data,
        ),
      };
    }

    return label;
  }) as HierarchicalLabelOrFolder[];
};

export const handleUpdateLabel = (
  set: ZustandSet,
  id: string,
  emailAddress: string,
  data: Partial<HierarchicalLabelOrFolder>,
) => {
  set((state) => ({
    labels: updateLabelRecursive(state.labels, id, emailAddress, data),
  }));
};

const deleteLabelRecursive = (
  labels: HierarchicalLabelOrFolder[],
  id: string,
): HierarchicalLabelOrFolder[] => {
  return labels
    .filter((label) => getItemId(label) !== id)
    .map((label) => {
      if (label.children && label.children.length > 0) {
        return {
          ...label,
          children: deleteLabelRecursive(
            label.children as HierarchicalLabelOrFolder[],
            id,
          ),
        };
      }
      return label;
    }) as HierarchicalLabelOrFolder[];
};

export const handleDeleteLabel = (set: ZustandSet, id: string) => {
  set((state) => ({
    labels: deleteLabelRecursive(state.labels, id),
  }));
};

const addLabelToParent = (
  labels: HierarchicalLabelOrFolder[],
  parentId: string,
  newLabel: HierarchicalLabelOrFolder,
): HierarchicalLabelOrFolder[] => {
  return labels.map((label) => {
    if (getItemId(label) === parentId) {
      return {
        ...label,
        children: [...(label.children || []), newLabel],
      };
    }
    if (label.children && label.children.length > 0) {
      return {
        ...label,
        children: addLabelToParent(
          label.children as HierarchicalLabelOrFolder[],
          parentId,
          newLabel,
        ),
      };
    }
    return label;
  }) as HierarchicalLabelOrFolder[];
};

export const handleCreateLabel = (
  set: ZustandSet,
  data: Omit<HierarchicalLabelOrFolder, 'children'>,
  parentId?: string,
) => {
  const isLabel = 'labelId' in data;
  const itemId = isLabel
    ? (
        data as Omit<HierarchicalLabelOrFolder, 'children'> & {
          labelId: string;
        }
      ).labelId
    : (
        data as Omit<HierarchicalLabelOrFolder, 'children'> & {
          id: string;
        }
      ).id;

  const newLabel = {
    ...data,
    ...(isLabel ? { labelId: itemId || nanoid() } : { id: itemId || nanoid() }),
    children: [],
  } as unknown as HierarchicalLabelOrFolder;

  if (parentId) {
    set((state) => ({
      labels: addLabelToParent(state.labels, parentId, newLabel),
    }));
  } else {
    set((state) => ({
      labels: [...state.labels, newLabel],
    }));
  }
};

export const handleGetLabelNameFromLabelIdsArray = (
  get: ZustandGet,
  emailAddress: string,
  ids?: string[],
): HierarchicalLabelOrFolder | undefined => {
  const labelId = ids?.find((str) => str.includes('Label_'));
  if (!labelId) {
    return undefined;
  }

  const label = handleGetLabelByIdAndEmailAddress(get, labelId, emailAddress);
  if (!label) {
    return undefined;
  }

  return label;
};

export const handleClearLabelsByEmailAddress = (
  set: ZustandSet,
  emailAddress: string,
) => {
  set((state) => ({
    labels: state.labels.filter((label) => label.emailAddress !== emailAddress),
  }));
};
